import { setContext } from '@apollo/client/link/context';

let authorization: string | undefined;

export const setAuthorization = (value?: string): void => {
  authorization = value ? `Bearer ${value}` : undefined;
};

/**
 * Operations the marketplace API serves to anonymous callers. They are sent
 * without a bearer token: the API verifies any token that is present and
 * answers 401 for an invalid or expired one, which would otherwise break e.g.
 * renewing an expired session or logging in again with a stale token stored.
 */
export const PUBLIC_OPERATIONS = new Set([
  'ApproveRegistration',
  'ForgotPassword',
  'Login',
  'Logout',
  'RefreshToken',
  'Register',
  'RegistrationReview',
  'RejectRegistration',
  'ResetPassword',
]);

type UnauthorizedListener = () => void;

const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Subscribe to "the API rejected our token" (HTTP 401). Returns an unsubscribe function. */
export const onUnauthorized = (
  listener: UnauthorizedListener,
): (() => void) => {
  unauthorizedListeners.add(listener);

  return () => {
    unauthorizedListeners.delete(listener);
  };
};

export const notifyUnauthorized = (): void => {
  unauthorizedListeners.forEach((listener) => listener());
};

// Marketplace users authenticate with the bearer token only. The X-auth-origin
// header is reserved for platform (cockpit) users and must not be sent here.
const authLink = setContext(({ operationName }, { headers }) => {
  const sendToken =
    authorization && !(operationName && PUBLIC_OPERATIONS.has(operationName));

  return {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    headers: {
      ...headers,
      ...(sendToken ? { authorization } : {}),
    },
  };
});

export default authLink;
